import { ColumnType } from 'it/primatoo/openapi/api/org/schemas/common/ColumnType';
import { QuerySpecificationUtils, Specification} from '../../utils/query-specification.utils';
import { TableFilterItem } from './table-filter-item';

export class TableFilterUtils {
  /**
   * Esegue una serie di test di debug sulla classe TableFilterUtils.
   * I risultati vengono mostrati in console.
   */

  public static formatTimeStamp(operator: string, value: string): number {
    switch (operator) {
      case QuerySpecificationUtils.mappingQuery('EQUAL'):
      case '=':
        return TableFilterUtils.toTimestamp(value) || Date.now();

      case QuerySpecificationUtils.mappingQuery('LESS_OR_EQUAL'):
      case '<=':
        return TableFilterUtils.toTimestampAt(value, 'beforeMidnight');

      case QuerySpecificationUtils.mappingQuery('GREATER_OR_EQUAL'):
      case '>=':
        return TableFilterUtils.toTimestampAt(value, 'midnight');

      default:
        console.warn('Unknown operator in formatTimeStamp:', operator);
        return TableFilterUtils.toTimestamp(value) || Date.now();
    }
  }

  public static toTimestampAt(
    value: string,
    timeMode: 'midnight' | 'beforeMidnight'
  ): number | undefined {
    try {
      const parseDate = _stringToDateNumber(value);
      const date = new Date(parseDate.year, parseDate.month, parseDate.day);
      switch (timeMode) {
        case 'midnight':
          date.setHours(0, 0, 0);
          break;
        case 'beforeMidnight':
          date.setHours(23, 59, 59);
          break;
      }
      return date.getTime();
    } catch (e) {
      return Date.now();
    }
  }

  public static toQuery(request: TableFilterItem[]): string | undefined {
    let response = '';
    let lastOperator: string;
    if (!(request.length === 0 || request.length % 3 !== 0)) {
      request.forEach((r, index) => {
        switch (r.type) {
          case 'column':
            if (response.length > 0) {
              response += ',' + r.column;
            } else {
              response += r.column;
            }
            break;
          case 'operator':
            {
              const query = QuerySpecificationUtils.mappingQuery(
                r.value as Specification
              );
              if (query) {
                response += query;
                lastOperator = query;
              } else {
                console.error('Invalid operator ' + r.value);
              }
            }
            break;
          case 'value':
            if (request[index - 2].columnType === 'DATE') {
              response += TableFilterUtils.formatTimeStamp(
                lastOperator,
                r.value
              );
            } else {
              response += r.value;
            }
            break;
        }
      });
    }
    return response;
  }

  public static fromQuery(query: string): TableFilterItem[] {
    if (!query || query.trim() === '') return [];

    const items: TableFilterItem[] = [];

    // Divide le condizioni per spazio o virgola
    const conditions = query.split(/\s+/).filter(Boolean);

    // Mappa simboli/operatori al nome logico
    const operatorMapping: Record<string, string> = {
      '=': 'EQUAL',
      '!=': 'NOT_EQUAL',
      '>': 'GREATER_THAN',
      '<': 'LESS_THAN',
      '>=': 'GREATER_OR_EQUAL',
      '<=': 'LESS_OR_EQUAL',
      'LIKE': 'LIKE',
      '~:': 'LIKE',       // aggiunto per operatori come internalId~:abc
      '::': 'EQUAL',      // aggiunto per operatori come docId::10
      ':': 'EQUAL',       // fallback per operatori base
    };

    // Regex aggiornato per supportare anche ~: e ::
    const operatorRegex = /(~:|::|>=|<=|!=|=|>|<|LIKE|:)/i;

    for (const cond of conditions) {
      const match = cond.match(operatorRegex);

      if (!match) {
        console.error(`Invalid condition: ${cond}`);
        continue;
      }

      const operatorSymbol = match[0];
      const [column, rawValue] = cond.split(operatorSymbol);
      const value = rawValue?.trim() ?? '';

      // Deduce il tipo colonna
      const columnType = this.detectColumnType(value);

      // Crea i tre elementi
      items.push(
        {
          type: 'column',
          value: column.trim(),
          column: column.trim(),
          columnType,
        },
        {
          type: 'operator',
          value:
            operatorMapping[operatorSymbol.toUpperCase()] ??
            operatorMapping[operatorSymbol] ??
            operatorSymbol,
          columnType,
        },
        {
          type: 'value',
          value,
          columnType,
        }
      );
    }

    return items;
  }

  /**
   * Deduce il tipo di colonna a partire dal valore testuale.
   */
  public static detectColumnType(value: string): ColumnType {
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value.trim()))  return ColumnType.DATE;
    if (/^\d+(\.\d+)?$/.test(value)) return ColumnType.NUMBER;
    if (/^(true|false)$/i.test(value)) return ColumnType.BOOLEAN;
    if (/^\d+(\.\d+)?\s?(KB|MB|GB)$/i.test(value)) return ColumnType.FILE_SIZE;
    return ColumnType.TEXT;
  }

  public static toTimestamp(value?: string | null): number | null {
    if (value) {
      try {
        const parseDate = _stringToDateNumber(value);
        const date = new Date();
        date.setDate(parseDate.day);
        date.setMonth(parseDate.month);
        date.setFullYear(parseDate.year);
        return date.getTime();
      } catch (e) {
        // return Date.now();
      }
    }
    return null;
  }
}

function _stringToDateNumber(value: string) {
  const day = Number.parseInt(value.split('/')[0]);
  const month = Number.parseInt(value.split('/')[1]) - 1;
  const year = Number.parseInt(value.split('/')[2]);
  return { day, month, year };
}
